export default class BeforeAfterResults {
  constructor(section) {
    this.section = section;
    this.slides = Array.from(section.querySelectorAll("[data-before-after-results-slide]"));
    this.textSlides = Array.from(section.querySelectorAll("[data-before-after-results-text-slide]"));

    this.order = [];
    this.activeIndex = 0;
    this.textActiveIndex = 0;

    this.isAnimating = false;
    this.isTextAnimating = false;
    this.hasRevealed = false;
    this.animationDuration = 1550;
    this.textAnimationDuration = 820;

    if (!this.slides.length) {
      return;
    }

    this.init();
  }

  init() {
    const total = this.slides.length;
    this.order = [];

    for (let i = 0; i < total; i++) {
      this.order.push((this.activeIndex - 3 + i + total * 4) % total);
    }

    // Apply slots instantly (no animation on init)
    this.slides.forEach((slide) => slide.classList.add("is-instant"));
    this.applySlots();

    window.requestAnimationFrame(() => {
      this.slides.forEach((slide) => slide.classList.remove("is-instant"));
    });

    this.section.querySelectorAll("[data-before-after-results-prev]").forEach((button) => {
      button.addEventListener("click", (event) => {
        event.preventDefault();
        button.blur();
        this.reveal();
        this.navigate("next");
      });
    });

    this.section.querySelectorAll("[data-before-after-results-next]").forEach((button) => {
      button.addEventListener("click", (event) => {
        event.preventDefault();
        button.blur();
        this.reveal();
        this.navigate("prev");
      });
    });

    this.section.querySelectorAll("[data-before-after-results-text-prev]").forEach((button) => {
      button.addEventListener("click", (event) => {
        event.preventDefault();
        button.blur();
        this.goToText(this.textActiveIndex + 1);
      });
    });

    this.section.querySelectorAll("[data-before-after-results-text-next]").forEach((button) => {
      button.addEventListener("click", (event) => {
        event.preventDefault();
        button.blur();
        this.goToText(this.textActiveIndex - 1);
      });
    });

    window.addEventListener("resize", () => {
      this.renderText();
    });

    this.textSlides.forEach((slide) => {
      slide.addEventListener("click", () => {
        if (this.isTextAnimating) {
          return;
        }

        const slot = slide.dataset.beforeAfterResultsTextSlot;
        const offset = slot !== "hidden" ? Number(slot) : null;

        // Only non-active visible slides are clickable (offset ±1 and ±2)
        if (offset === null || offset === 0 || Math.abs(offset) > 2) {
          return;
        }

        const total = this.textSlides.length;
        // offset > 0 → slide is to the right → advance forward (index increases)
        // offset < 0 → slide is to the left → go backward
        const targetIndex = (this.textActiveIndex + offset + total) % total;
        this.goToText(targetIndex);
      });
    });

    this.slides.forEach((slide) => {
      slide.addEventListener("click", () => {
        if (this.isAnimating) {
          return;
        }

        const slot = slide.dataset.beforeAfterResultsSlot;

        if (slot !== "prev-1" && slot !== "prev-2" && slot !== "prev-3") {
          return;
        }

        this.reveal();

        // navigate("prev") rotates order RIGHT by 1:
        // prev-1→active in 1 step, prev-2→active in 2, prev-3→active in 3.
        const stepsMap = { "prev-1": 1, "prev-2": 2, "prev-3": 3 };
        const steps = stepsMap[slot] || 1;

        if (steps === 1) {
          // Adjacent slide: full smooth animation identical to nav button
          this.navigate("prev");
        } else {
          // Farther slides: fast smooth transition (~0.65s)
          this.goDirectly(steps);
        }
      });
    });

    this.observeEntrance();
    this.renderText();
  }

  observeEntrance() {
    if (!("IntersectionObserver" in window)) {
      this.reveal();
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) {
            return;
          }

          this.reveal();
          observer.unobserve(this.section);
        });
      },
      {
        rootMargin: "0px 0px -35% 0px",
        threshold: 0.24,
      },
    );

    observer.observe(this.section);
  }

  reveal() {
    if (this.hasRevealed) {
      return;
    }

    this.hasRevealed = true;
    this.section.classList.add("is-revealing");
    this.applySlots();
    this.section.classList.add("is-ready");

    window.setTimeout(() => {
      this.section.classList.remove("is-revealing");
    }, this.animationDuration + 520);
  }

  /**
   * Navigate one step.
   * "next" = prev-button clicked: rotate order left, wrap-around slide enters from right.
   * "prev" = next-button clicked: rotate order right, new slide enters from left.
   */
  navigate(direction) {
    if (this.isAnimating || this.slides.length < 2) {
      return;
    }

    this.isAnimating = true;
    this.section.classList.add("is-switching");

    if (direction === "next") {
      const outgoing = this.order.shift();
      const outgoingSlide = this.slides[outgoing];

      // Teleport the wrap-around slide to entering-right (inactive size, right of active).
      // It will then animate smoothly into the active position.
      outgoingSlide.classList.add("is-instant");
      outgoingSlide.dataset.beforeAfterResultsSlot = "entering-right";
      void outgoingSlide.offsetWidth;
      outgoingSlide.classList.remove("is-instant");

      this.order.push(outgoing);
      this.activeIndex = this.order[3];

      // Fix for >4 slides: if the new active slide is still at its hidden default
      // position (-220px), teleport it to entering-right so it enters from the
      // right side instead of animating through the visible area.
      const newActiveSlide = this.slides[this.activeIndex];
      const newActiveSlot = newActiveSlide.dataset.beforeAfterResultsSlot;
      if (!newActiveSlot || newActiveSlot === "hidden") {
        newActiveSlide.classList.add("is-instant");
        newActiveSlide.dataset.beforeAfterResultsSlot = "entering-right";
        void newActiveSlide.offsetWidth;
        newActiveSlide.classList.remove("is-instant");
      }

    } else {
      const incoming = this.order.pop();
      const incomingSlide = this.slides[incoming];

      // Teleport incoming slide instantly to entering-left (off-screen left) so it enters smoothly.
      incomingSlide.classList.add("is-instant");
      incomingSlide.dataset.beforeAfterResultsSlot = "entering-left";
      void incomingSlide.offsetWidth;
      incomingSlide.classList.remove("is-instant");

      this.order.unshift(incoming);
      this.activeIndex = this.order[3];
    }

    // Apply new slots (with animation)
    this.applySlots();

    window.setTimeout(() => {
      this.isAnimating = false;
      this.section.classList.remove("is-switching");
      // Clean up — remove any leftover entering/exiting states
      this.applySlots();
    }, this.animationDuration);
  }

  /**
   * Jump to the slide N right-rotations away with a fast smooth transition.
   * Used for prev-2 and prev-3 clicks where full-speed animation would be too slow.
   */
  goDirectly(steps) {
    if (this.isAnimating || this.slides.length < 2 || steps <= 0) {
      return;
    }

    const FAST_DURATION = 650;

    this.isAnimating = true;
    this.section.classList.add("is-switching");

    // Rotate order RIGHT by `steps` (same direction as navigate("prev"))
    for (let i = 0; i < steps; i++) {
      const incoming = this.order.pop();
      this.order.unshift(incoming);
    }
    this.activeIndex = this.order[3];

    // Apply fast transition on all slides via inline style override
    const fastStyle = `transition-duration: ${FAST_DURATION}ms !important`;
    this.slides.forEach((slide) => slide.setAttribute("style", fastStyle));
    this.applySlots();

    window.setTimeout(() => {
      // Remove inline style overrides
      this.slides.forEach((slide) => slide.removeAttribute("style"));
      this.isAnimating = false;
      this.section.classList.remove("is-switching");
      this.applySlots();
    }, FAST_DURATION + 50);
  }

  goToText(index) {
    if (this.isTextAnimating || this.textSlides.length < 2) {
      return;
    }

    const total = this.textSlides.length;
    const nextIndex = (index + total) % total;

    if (nextIndex === this.textActiveIndex) {
      return;
    }

    this.isTextAnimating = true;
    this.textActiveIndex = nextIndex;
    this.renderText();

    window.setTimeout(() => {
      this.isTextAnimating = false;
    }, this.textAnimationDuration);
  }

  /**
   * Assigns data-slot attributes based on current virtual order.
   * Virtual positions: 0 = prev-3, 1 = prev-2, 2 = prev-1, 3 = active, rest = hidden.
   *
   * Key rule: if a slide is transitioning from entering-right/entering-left to hidden,
   * snap it instantly to avoid the visible "fly-back" artifact.
   */
  applySlots() {
    const SLOT_NAMES = ["prev-3", "prev-2", "prev-1", "active"];
    const TRANSIENT_SLOTS = new Set(["entering-right", "entering-left"]);
    const slotMap = new Map();

    this.order.forEach((slideIndex, visualPos) => {
      slotMap.set(slideIndex, SLOT_NAMES[visualPos] || "hidden");
    });

    this.slides.forEach((slide, index) => {
      const newSlot = slotMap.get(index) || "hidden";
      const currentSlot = slide.dataset.beforeAfterResultsSlot || "hidden";
      const isActive = newSlot === "active";

      if (currentSlot !== newSlot) {
        // If a slide was in a transient entry position and now goes hidden,
        // snap it instantly to prevent the visible reverse-fly artifact.
        if (TRANSIENT_SLOTS.has(currentSlot) && newSlot === "hidden") {
          slide.classList.add("is-instant");
          slide.dataset.beforeAfterResultsSlot = newSlot;
          void slide.offsetWidth;
          slide.classList.remove("is-instant");
        } else {
          slide.dataset.beforeAfterResultsSlot = newSlot;
        }
      }

      slide.classList.toggle("is-active", isActive);
    });
  }

  renderText() {
    if (!this.textSlides.length) {
      return;
    }

    const total = this.textSlides.length;

    this.textSlides.forEach((slide, index) => {
      const offset = this.getTextOffset(index, total);
      const isActive = offset === 0;
      const isVisible = Math.abs(offset) <= 2;
      const position = this.getTextPosition(offset);

      slide.style.setProperty("--before-after-results-text-x", `${position.x}px`);
      slide.style.setProperty("--before-after-results-text-y", `${position.y}px`);
      slide.style.setProperty("--before-after-results-text-scale", position.scale);
      slide.style.setProperty("--before-after-results-text-opacity", position.opacity);
      slide.dataset.beforeAfterResultsTextSlot = isVisible ? String(offset) : "hidden";
      slide.classList.toggle("is-active", isActive);
    });

    this.updateTextTrackHeight();
  }

  updateTextTrackHeight() {
    const visibleSlides = this.textSlides.filter(
      (slide) => slide.dataset.beforeAfterResultsTextSlot !== "hidden",
    );

    if (!visibleSlides.length) {
      return;
    }

    window.requestAnimationFrame(() => {
      const maxHeight = visibleSlides.reduce((height, slide) => {
        return Math.max(height, slide.scrollHeight);
      }, 0);

      this.section.style.setProperty("--before-after-results-text-height", `${maxHeight}px`);
    });
  }

  getTextOffset(index, total) {
    let offset = index - this.textActiveIndex;
    const half = Math.floor(total / 2);

    if (offset > half) {
      offset -= total;
    }

    if (offset < -half) {
      offset += total;
    }

    return offset;
  }

  getTextPosition(offset) {
    const isCompact = window.matchMedia("(max-width: 992px)").matches;
    const positions = isCompact
      ? {
          "-2": { x: -609, y: 10, scale: 0.9, opacity: 0.72 },
          "-1": { x: -312, y: 10, scale: 0.92, opacity: 0.72 },
          0: { x: 0, y: 0, scale: 1, opacity: 1 },
          1: { x: 312, y: 10, scale: 0.92, opacity: 0.72 },
          2: { x: 609, y: 10, scale: 0.9, opacity: 0.72 },
        }
      : {
          "-2": { x: -857, y: 12, scale: 0.92, opacity: 0.72 },
          "-1": { x: -437, y: 12, scale: 0.92, opacity: 0.72 },
          0: { x: 0, y: 0, scale: 1, opacity: 1 },
          1: { x: 437, y: 12, scale: 0.92, opacity: 0.72 },
          2: { x: 857, y: 12, scale: 0.92, opacity: 0.72 },
        };

    if (Object.prototype.hasOwnProperty.call(positions, offset)) {
      return positions[offset];
    }

    return {
      x: offset < 0 ? -1200 : 1200,
      y: 18,
      scale: 0.88,
      opacity: 0,
    };
  }
}

document.querySelectorAll(".before-after-results").forEach((section) => {
  new BeforeAfterResults(section);
});
